import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Checkbox, Heading, Label, Paragraph, Tag, Textarea } from '@digdir/designsystemet-react';
import { ArrowLeftIcon } from '@navikt/aksel-icons';
import type { TranslationEntry } from '../../../../api/hms';
import type { ChecklistLanguage, ChecklistTranslationItem } from '../../../../api/safetyRounds';
import { languageNameInText } from '../../../HRDashboard/hmsLanguages';
import { groupTranslations, textsToSuggest, translationLabel } from '../checklistView';
import layout from '../../../HRDashboard/hmsPage.module.css';
import classes from '../Checklists.module.css';

type Props = {
  language: ChecklistLanguage;
  onBack: () => void;
  onSave: (entries: TranslationEntry[]) => void;
  saving: boolean;
  saveFailed: boolean;
  onSuggest: () => void;
  suggesting: boolean;
  suggestFailed: boolean;
};

/**
 * Every text of the checklist next to its translation. A Trustpulse text is used until the
 * organisation writes its own, and an AI suggestion counts only when HR approves it.
 */
export const ChecklistTranslationPanel = ({
  language,
  onBack,
  onSave,
  saving,
  saveFailed,
  onSuggest,
  suggesting,
  suggestFailed,
}: Props): ReactElement => {
  const { t } = useTranslation();
  const K = 'safetyRounds.checklist.translate';
  const [missingOnly, setMissingOnly] = useState(false);
  // What HR has typed, by key, until it is saved
  const [editing, setEditing] = useState<Record<string, string>>({});
  const name = languageNameInText(language, t);
  const shown = language.items.filter((item) => !missingOnly || !item.translated);
  const todo = textsToSuggest(language.items);
  const drafts = language.items.filter((item) => item.aiDraft);
  const changed = Object.keys(editing).length > 0;

  const value = (item: ChecklistTranslationItem) => editing[item.key] ?? item.text;

  const save = () => {
    onSave(
      Object.entries(editing).map(([key, text]) => ({ key, text, aiDraft: false })),
    );
    setEditing({});
  };

  const approve = (items: ChecklistTranslationItem[]) =>
    onSave(items.map((item) => ({ key: item.key, text: value(item), aiDraft: false })));

  return (
    <section aria-labelledby="checklist-translate" className={layout.card} data-gap="md">
      <Button data-size="sm" variant="tertiary" className={layout.startAligned} onClick={onBack}>
        <ArrowLeftIcon aria-hidden="true" />
        {t(`${K}.back`)}
      </Button>
      <div className={layout.cardHeading}>
        <Heading level={2} data-size="sm" id="checklist-translate">
          {t(`${K}.title`, { language: name })}
        </Heading>
        <Paragraph data-size="sm" className={layout.cardIntro}>
          {t(`${K}.intro`, { translated: language.translated, total: language.total })}
        </Paragraph>
      </div>
      <div className={layout.actions}>
        {todo.length > 0 && (
          <Button data-size="sm" variant="secondary" onClick={onSuggest} aria-busy={suggesting || undefined}>
            {t(`${K}.suggest`, { count: todo.length })}
          </Button>
        )}
        {drafts.length > 0 && (
          <Button data-size="sm" variant="secondary" onClick={() => approve(drafts)}>
            {t(`${K}.approveAll`, { count: drafts.length })}
          </Button>
        )}
      </div>
      {suggestFailed && (
        <Alert data-color="danger" data-size="sm">
          <Paragraph data-size="sm">{t(`${K}.suggestFailed`)}</Paragraph>
        </Alert>
      )}
      <Checkbox
        data-size="sm"
        label={t(`${K}.missingOnly`)}
        checked={missingOnly}
        onChange={(event) => setMissingOnly(event.target.checked)}
      />
      {groupTranslations(shown).map((group, groupIndex) => (
        <div key={`${group.title ?? 'template'}-${groupIndex}`} className={classes.translationItem}>
          <Heading level={3} data-size="2xs">
            {group.title ?? t(`${K}.template`)}
          </Heading>
          {group.items.map((item) => (
            <div key={item.key} className={classes.translationItem}>
              <Label htmlFor={`translate-${item.key}`} data-size="sm">
                {translationLabel(item, t)}
              </Label>
              <Paragraph data-size="sm" className={classes.source} lang="nb">
                {item.source}
              </Paragraph>
              <Textarea
                id={`translate-${item.key}`}
                rows={2}
                lang={language.code}
                dir={language.direction}
                value={value(item)}
                placeholder={item.standard ?? ''}
                onChange={(event) => setEditing({ ...editing, [item.key]: event.target.value })}
              />
              {item.standard !== null && !value(item).trim() && (
                <Paragraph data-size="xs" className={layout.subtle}>
                  {t(`${K}.standardUsed`)}
                </Paragraph>
              )}
              {item.aiDraft && editing[item.key] === undefined && (
                <div className={layout.actions}>
                  <Tag data-color="warning" data-size="sm">
                    {t(`${K}.aiDraft`)}
                  </Tag>
                  <Button data-size="sm" variant="tertiary" onClick={() => approve([item])}>
                    {t(`${K}.approve`)}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
      {saveFailed && (
        <Alert data-color="danger" data-size="sm">
          <Paragraph data-size="sm">{t('hms.errors.save')}</Paragraph>
        </Alert>
      )}
      {changed && (
        <div className={layout.actions}>
          <Button data-size="sm" onClick={save} aria-busy={saving || undefined}>
            {t(`${K}.save`)}
          </Button>
          <Button data-size="sm" variant="tertiary" onClick={() => setEditing({})}>
            {t('safetyRounds.cancel')}
          </Button>
        </div>
      )}
    </section>
  );
};
